import * as React from 'react';
import { useDirtyStateContext } from '../contexts/DirtyStateContext';

let nextAutoId = 0;

/**
 * useUnsavedChanges - registers a dirty source in the global DirtyStateContext.
 *
 * The DirtyStateContext handles all side effects centrally:
 *   - Single useBlocker for React Router in-app navigation blocking
 *   - Single beforeunload handler for browser refresh / close / external nav
 *   - Exposes isAnyDirty for page-title dirty markers (via usePageTitle)
 *
 * ── Forms that call navigate() on successful save ──────────────────────────
 * If the parent's onSubmit handler performs a React Router navigation after a
 * successful save, the caller MUST pass `false` for isDirty while the
 * submission is in flight. Otherwise the blocker will intercept the
 * programmatic navigation and show a spurious warning.
 *
 * The recommended pattern is to gate isDirty on the local isSubmitting flag:
 *
 * ```tsx
 * const [isSubmitting, setIsSubmitting] = React.useState(false);
 * const isDirty = !isSubmitting && (name !== initialRef.current.name);
 * useUnsavedChanges(isDirty);
 *
 * const handleSubmit = async () => {
 *   setIsSubmitting(true);      // disarms the blocker before the first await
 *   try {
 *     await onSubmit(data);     // onSubmit may call navigate() internally
 *   } catch {
 *     setIsSubmitting(false);   // re-arms the blocker if save fails
 *   }
 * };
 * ```
 *
 * @param isDirty - true when the form has unsaved changes
 * @param key - optional stable key for this dirty source. Auto-generated if omitted.
 *   Use a scoped key like 'rr:settings' for right-rail forms so the rail can
 *   guard close/switch actions independently via `hasDirtyInScope('rr:')`.
 * @param label - optional human-readable name shown in the confirm dialog (e.g. 'Create customer').
 */
export function useUnsavedChanges(isDirty: boolean, key?: string, label?: string): void {
  const { register, unregister } = useDirtyStateContext();
  const keyRef = React.useRef(key ?? `unsaved-${nextAutoId++}`);

  React.useEffect(() => {
    register(keyRef.current, isDirty, label);
  }, [isDirty, label, register]);

  React.useEffect(() => {
    const k = keyRef.current;
    return () => unregister(k);
  }, [unregister]);
}
